import { useEffect, useState } from "react";
import { Loader2, MailWarning } from "lucide-react";
import { toast } from "sonner";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog";
import { Panel } from "@/components/Panel";
import { http, formatError, money } from "@/lib/api";

const STAGE_TONE = { polite: "text-emerald-300 border-emerald-500/40", firmer: "text-amber-300 border-amber-500/40", firm: "text-orange-300 border-orange-500/50", final: "text-red-300 border-red-500/50" };
const Stage = ({ s, testId }) => <span data-testid={testId} className={`text-[10px] font-semibold uppercase tracking-[0.15em] px-3 py-1 rounded-full border ${STAGE_TONE[s] || "text-white/70 border-white/20"}`}>{s}</span>;

export const OverduePanel = () => {
  const [data, setData] = useState(null);
  const [ask, setAsk] = useState(false);
  const [busy, setBusy] = useState(false);
  const load = () => http.get("/omvexis/overdue").then((r) => setData(r.data)).catch(() => setData({ items: [], error: true }));
  useEffect(() => { load(); }, []);
  const due = (data?.items || []).filter((i) => i.action?.startsWith("will send")).length;
  const run = async () => {
    setBusy(true);
    try { const { data: r } = await http.post("/omvexis/escalation/run"); toast.success(`${r.sent} buyer reminder(s) sent · team list emailed`); setData(r); }
    catch (e) { toast.error(formatError(e)); }
    setBusy(false);
  };
  return (
    <Panel label="Escalation · day 1 polite → 7 firmer → 15 firm → 30 final" title={`Overdue Payments${data ? ` · ${money(data.total_overdue)}` : ""}`} testId="omx-overdue-panel"
      right={<button className="hud-btn" disabled={busy || !due} onClick={() => setAsk(true)} data-testid="overdue-send-reminders">{busy ? <Loader2 size={13} className="animate-spin" /> : <MailWarning size={13} />} Send {due} due reminder{due === 1 ? "" : "s"}</button>}>
      <p className="text-slate-400 text-sm mb-4">Every morning at 08:00 JARVIS emails each overdue buyer the reminder for their stage (once per stage) and sends your team the full overdue list.</p>
      {!data ? <Loader2 className="animate-spin text-slate-500" size={16} /> : data.items.length === 0 ? (
        <p className="text-slate-500 text-sm" data-testid="overdue-empty">{data.error ? "Could not read Omvexis shipments." : "No overdue payments. Splendid, sir."}</p>
      ) : (
        <div className="overflow-x-auto"><table className="w-full text-left">
          <thead><tr className="border-b border-cyan-500/30">{["Ref", "Buyer", "Amount", "Due", "Overdue", "Stage", "Next step"].map((h) => <th key={h} className="hud-label py-2 px-3 font-normal">{h}</th>)}</tr></thead>
          <tbody>{data.items.map((i) => (
            <tr key={i.shipment_id} className="border-b border-cyan-900/50 hover:bg-cyan-950/30" data-testid={`overdue-row-${i.reference}`}>
              <td className="py-2 px-3 font-mono-hud text-[12px] text-cyan-300">{i.reference}</td>
              <td className="px-3"><div className="text-slate-100">{i.buyer}</div><div className="font-mono-hud text-[11px] text-slate-500">{i.buyer_email || "no email"}</div></td>
              <td className="px-3">{money(i.amount)}</td><td className="px-3 font-mono-hud text-[12px]">{i.due}</td>
              <td className="px-3 text-red-300 font-display">{i.days}d</td>
              <td className="px-3"><Stage s={i.stage} testId={`overdue-stage-${i.reference}`} /></td>
              <td className="px-3 text-sm text-slate-300" data-testid={`overdue-action-${i.reference}`}>{i.action}</td>
            </tr>))}</tbody>
        </table></div>
      )}
      <AlertDialog open={ask} onOpenChange={setAsk}>
        <AlertDialogContent data-testid="overdue-confirm-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Email {due} buyer reminder{due === 1 ? "" : "s"} now?</AlertDialogTitle>
            <AlertDialogDescription>Real emails go to each overdue buyer, worded for their stage, and your team gets the overdue list.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="overdue-confirm-cancel">Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={run} data-testid="overdue-confirm-send">Send reminders</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </Panel>
  );
};
